/**
 * hexAlerts.core — the node-shape → shared-contract mapping the cloud's alert-board
 * adapters share.
 *
 * There are two of them: `hexAlerts.adapter` (ONE node, the site's own board) and
 * `multiSiteHexAlerts.adapter` (EVERY node, the all-sites board). They differ only in how
 * many nodes they fan out to — the shapes coming back over the tunnel are identical, and a
 * second private copy of "what counts as online", "which severities are critical" or "how a
 * snapshot is addressed" is exactly how the two boards would start disagreeing about the
 * same alert.
 */
import type { HexAlert, HexCamera, HexCounter } from '@trinetra/shared';

export const emptyCounter = (): HexCounter => ({ critical: 0, warning: 0, normal: 0, total: 0 });

/** An alert still needs attention only while untouched ('new'); ack/resolve/cancel drop it. */
export const isActive = (status?: string) => !status || status === 'new';

/**
 * Online = the camera's stream is actually flowing (`streaming`/`recording`/`active`).
 * `connecting` (the pipeline's reconnect loop) and `error`/`inactive` are NOT online — a
 * dead camera loops error→connecting forever, so it must read offline. The shared page
 * debounces the offline transition so a healthy camera that briefly dips through
 * `connecting` between streaming stretches doesn't flicker. cameras.status is the
 * authoritative signal — `is_recording`/`live_pipeline` only cover cameras recording or
 * being live-viewed right now, not ones simply up but unwatched.
 */
export const isOnline = (status?: string) =>
  status === 'streaming' || status === 'recording' || status === 'active';

// Cloud severities (low/medium/high/critical) → the 3 hex tiers.
export function toSeverity(s?: string): HexAlert['severity'] {
  switch ((s || '').toLowerCase()) {
    case 'critical':
      return 'critical';
    case 'high':
    case 'medium':
    case 'warning':
      return 'warning';
    default:
      return 'normal'; // low / info
  }
}

/**
 * The logged-in user, read from AuthContext's localStorage mirror — this
 * module isn't a React component, so it can't use useAuth(). Ack/resolve must
 * carry the actor or the alert's Activity feed shows "Unknown".
 */
export function currentUser(): { id: string | null; name: string | null } {
  try {
    const u = JSON.parse(localStorage.getItem('cloud_user') || 'null');
    return { id: u?.id ?? null, name: u?.name ?? u?.email ?? null };
  } catch {
    return { id: null, name: null };
  }
}

/** One node camera row → the shared HexCamera, placed in the site the caller names. */
export function toHexCamera(c: any, siteId: string, siteName: string | null): HexCamera {
  const groupId = c.groups?.[0]?.id ?? null;
  const groupName = c.groups?.[0]?.name ?? null;
  const live = isOnline(c.status) || c.live_pipeline === true;
  const recording = isOnline(c.status) || c.is_recording === true;
  return {
    id: c.id,
    name: c.name,
    live,
    recording,
    online: live || recording,
    groupId,
    groupName,
    siteId,
    siteName,
  };
}

// Snapshot image types the AI pipeline emits, in card-preview preference order.
const PREVIEW_IMAGE_TYPES = ['fullFrame', 'personCrop', 'faceCrop'];

/**
 * Cloud can't load the node's saved snapshot directly — `image_urls`/`snapshot_path` point at
 * the node's nginx `/media/snapshots/…`, reachable only on the LAN. The image is blob-fetched
 * through the tunnel via the analytics event-image proxy, which keys on the SOURCE model +
 * event id + image type, NOT the alert id. So when we map a DB alert row we stash exactly what
 * loadPreview() needs, keyed by alert id. Slim socket broadcasts that omit these fields get no
 * entry ⇒ loadPreview() returns null (the card keeps its "No preview" strip until a DB-backed
 * row for the same alert replaces it on the next listActiveAlerts/snapshot fetch).
 *
 * Keyed by alert id alone, so it is shared by both adapters: alert ids are per-node UUIDs and
 * an operator moving between a site's board and the all-sites board keeps whatever either has
 * already resolved.
 */
interface PreviewMeta {
  model: string;
  eventId: string;
  imageType: string;
}
const previewMeta = new Map<string, PreviewMeta>();

export const getPreviewMeta = (alertId: string) => previewMeta.get(alertId);

/**
 * Record how to fetch an alert's snapshot. Called from two places: mapAlert, for DB-backed
 * rows that already carry the image map, and the `alert:preview` handler, for live alerts —
 * which arrive with NO images at all (the node broadcasts before its storage lane has written
 * the event row) and get their entry only once the node says the snapshot is stored.
 */
export function rememberPreview(alertId?: string, sourceTable?: string, eventId?: string, images?: any): boolean {
  if (!alertId || !eventId) return false;
  const model = tableToModel(sourceTable);
  const imageType = pickImageType({ image_urls: images });
  if (!model || !imageType) return false;
  previewMeta.set(alertId, { model, eventId, imageType });
  return true;
}

/** Map an alert's `source_table` to the analytics event `model` the image proxy expects.
 *  Most tables just drop the `_events` suffix; the irregular ones are listed explicitly. */
const TABLE_TO_MODEL: Record<string, string> = {
  person_events: 'person',
  person_count_events: 'person_count',
  intrusion_events: 'intrusion',
  theft_events: 'theft',
  fall_detection_events: 'fall',
  facial_recognition_events: 'face',
  ppe_violation_events: 'ppe_violation',
  vehicle_crossing_events: 'vehicle_crossing',
  // Events no typed table covers (panel OCR, belt movement, …): the AI Analytics report model.
  // Suffix-dropping gave 'ai', which the node rejects as an unknown model — so those alerts and
  // their event records showed no image on the cloud.
  ai_events: 'ai_analytics',
};
export function tableToModel(sourceTable?: string): string | null {
  if (!sourceTable) return null;
  return TABLE_TO_MODEL[sourceTable] || sourceTable.replace(/_events$/, '') || null;
}

/** Pick the best available snapshot type for the card preview from the row's image map. */
function pickImageType(a: any): string | null {
  const imgs = a.image_urls || a.image_paths || {};
  const keys = Object.keys(imgs);
  if (keys.length === 0) return null;
  return PREVIEW_IMAGE_TYPES.find((t) => keys.includes(t)) || keys[0];
}

/**
 * Normalize one alert to the shared HexAlert. Handles BOTH shapes: the REST snapshot
 * (camera_id / event_timestamp / summary / id) and the live socket broadcast pushed by
 * the local backend (camera_id / timestamp / alert_name / alert_description / alert_id).
 */
export function mapAlert(a: any): HexAlert {
  const id = a.id ?? a.alert_id;
  const eventId = a.event_id ?? a.eventId;
  // Remember how to lazily blob-fetch this alert's saved snapshot (model + eventId + imageType).
  // Only DB rows carry an image map; a LIVE alert has none by design and gets its entry later,
  // from the `alert:preview` broadcast the node sends once storage has committed.
  rememberPreview(id, a.source_table, eventId, a.image_urls || a.image_paths);
  return {
    id,
    cameraId: a.camera_id,
    severity: toSeverity(a.severity),
    eventType: a.event_type,
    // The card prints the name on one line and the description under it, so these two
    // must not resolve to the same string. They used to: both read `summary`, and the
    // card spent two lines saying "Fall detected" twice.
    //
    // The name is `alert_name` from BOTH sides now — the node decides it when the alert
    // fires and stores it on the row (migration 056), so a live broadcast and the same
    // alert re-read from the database finally agree. They did not before: the REST row
    // carried only the matched RULE's name, which is the one thing that cannot name a
    // watchlist hit, so a flagged person flashed up as "Missing Person" and came back
    // as "Face Recognized". `alert_config_name` stays behind it for rows written before
    // that column existed.
    alertName: a.alert_name || a.alert_config_name || a.event_type,
    description: a.summary || a.alert_description,
    snapshotUrl: null, // cloud snapshots aren't URL-addressable — fetched as a blob via loadPreview
    eventId, // source event id → used by loadPreview to pull the snapshot through the tunnel
    timestamp: a.event_timestamp ?? a.timestamp,
    status: a.status || 'new',
    escalationLevel: a.escalation_level ?? null,
    escalationState: a.escalation_state ?? null,
    occurrences: a.occurrences ?? null,
  };
}

export function buildCounters(recent: HexAlert[]): Record<string, HexCounter> {
  const counters: Record<string, HexCounter> = {};
  for (const a of recent) {
    if (!a.cameraId) continue;
    const c = (counters[a.cameraId] ||= emptyCounter());
    c[a.severity] += 1;
    c.total += 1;
  }
  return counters;
}
